import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearch } from "wouter";
import { useListStudents, useCreateStudent, useUpdateStudent, useArchiveStudent, getListStudentsQueryKey } from "@workspace/api-client-react";
import { Plus, Search, Archive, Pencil, Users, Upload, Download, X, ArchiveRestore, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiUrl } from "@/lib/apiBase";
import { getApiErrorMessage } from "@/lib/api-error";
import { ImportStudentsDialog } from "@/components/import-students-dialog";
import { DataMinimisationTip } from "@/components/DataMinimisationTip";
import {
  formatStudentName,
  studentSortKey,
  useDefaultStudentDisplayMode,
  STUDENT_DISPLAY_MODES,
  type StudentDisplayMode,
} from "@/lib/studentDisplay";

type Student = {
  id: number;
  firstName: string;
  lastName?: string | null;
  preferredName?: string | null;
  studentCode?: string | null;
  displayMode?: string | null;
  yearLevel?: string | null;
  notes?: string | null;
  schoolId?: number | null;
  archived?: boolean;
};

const DISPLAY_MODE_LABELS: Record<StudentDisplayMode, string> = {
  full:     "Full name (First Last)",
  first:    "First name only",
  initials: "Initials (F.L.)",
  nickname: "Preferred / nickname",
  code:     "Student code",
};

function StudentForm({ initial, onSave, onClose, isPending }: {
  initial?: Partial<Student>;
  onSave: (data: Omit<Student, "id">) => void;
  onClose: () => void;
  isPending: boolean;
}) {
  const [firstName, setFirstName] = useState(initial?.firstName ?? "");
  const [lastName, setLastName] = useState(initial?.lastName ?? "");
  const [preferredName, setPreferredName] = useState(initial?.preferredName ?? "");
  const [studentCode, setStudentCode] = useState(initial?.studentCode ?? "");
  // "" → use the account default; otherwise an explicit per-student
  // override that the helper preserves regardless of what default the
  // teacher later chooses in Settings.
  const [displayMode, setDisplayMode] = useState<string>(initial?.displayMode ?? "");
  const [yearLevel, setYearLevel] = useState(initial?.yearLevel ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) return;
    onSave({
      firstName: firstName.trim(),
      lastName:      lastName.trim()      || null,
      preferredName: preferredName.trim() || null,
      studentCode:   studentCode.trim()   || null,
      displayMode:   displayMode || null,
      yearLevel:     yearLevel.trim()     || null,
      notes:         notes.trim()         || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>First Name *</Label>
          <Input spellCheck={false} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label>
            Last Name <span className="text-gray-400 font-normal">(optional)</span>
          </Label>
          <Input spellCheck={false} value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>
            Preferred name <span className="text-gray-400 font-normal">(optional)</span>
          </Label>
          <Input
            spellCheck={false}
            value={preferredName}
            onChange={(e) => setPreferredName(e.target.value)}
            placeholder="e.g. Sam, Liz"
          />
        </div>
        <div className="space-y-1.5">
          <Label>
            Student code <span className="text-gray-400 font-normal">(optional)</span>
          </Label>
          <Input
            spellCheck={false}
            value={studentCode}
            onChange={(e) => setStudentCode(e.target.value)}
            placeholder="e.g. school ID, roll number"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Year Level</Label>
          <Input value={yearLevel} onChange={(e) => setYearLevel(e.target.value)} placeholder="e.g. 7, 10, 12" />
        </div>
        <div className="space-y-1.5">
          <Label>Display as</Label>
          <Select value={displayMode || "default"} onValueChange={(v) => setDisplayMode(v === "default" ? "" : v)}>
            <SelectTrigger>
              <SelectValue placeholder="Account default" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Account default</SelectItem>
              {STUDENT_DISPLAY_MODES.map((m) => (
                <SelectItem key={m} value={m}>{DISPLAY_MODE_LABELS[m]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Notes</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any relevant notes..." />
        <DataMinimisationTip />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={isPending} data-write-action>{isPending ? "Saving..." : "Save Student"}</Button>
      </DialogFooter>
    </form>
  );
}

function downloadBlob(content: string, filename: string, type = "text/csv") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Students() {
  const { data: students, isLoading, refetch } = useListStudents();
  const [pastOpen, setPastOpen] = useState(false);
  const { data: archivedListRaw, refetch: refetchArchived } = useListStudents(
    { includeArchived: "1" },
    { query: { enabled: pastOpen } },
  );
  const createStudent = useCreateStudent();
  const updateStudent = useUpdateStudent();
  const archiveStudent = useArchiveStudent();
  const { toast } = useToast();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const accountDefault = useDefaultStudentDisplayMode();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFilter, setExportFilter] = useState<"active" | "archived" | "all">("active");

  const archivedStudents = ((archivedListRaw ?? []) as Student[]).filter((s) => s.archived);

  // Task #143 — deep-link from the export-preview missing-code banner.
  // `?missing-code=true` shows only students without a `studentCode`
  // (the value that would force a fallback identifier in code-mode
  // exports). The teacher can clear the filter from the visible chip.
  const search$ = useSearch();
  const missingCodeOnly =
    new URLSearchParams(search$).get("missing-code") === "true";
  // Task #156 — sibling deep-link from the export-preview banner for
  // students with no name recorded (mirrors the server's `no_name`
  // fallback reason: no firstName *and* no lastName trimmed).
  const missingNameOnly =
    new URLSearchParams(search$).get("missing-name") === "true";
  const [missingCodeActive, setMissingCodeActive] = useState(missingCodeOnly);
  const [missingNameActive, setMissingNameActive] = useState(missingNameOnly);
  // Re-sync if the teacher navigates between filtered/unfiltered URLs.
  if (missingCodeOnly !== missingCodeActive) {
    setMissingCodeActive(missingCodeOnly);
  }
  if (missingNameOnly !== missingNameActive) {
    setMissingNameActive(missingNameOnly);
  }

  const filtered = (students ?? [])
    .filter((s) => {
      if (missingCodeActive) {
        const code = ((s as Student).studentCode ?? "").trim();
        if (code) return false;
      }
      if (missingNameActive) {
        // Per task: hide any student that has *any* name-shaped field
        // recorded — first, last, or preferred (preferredName covers
        // nickname in this model; see lib/student-display).
        const st = s as Student;
        const first = (st.firstName ?? "").trim();
        const last = (st.lastName ?? "").trim();
        const preferred = (st.preferredName ?? "").trim();
        if (first || last || preferred) return false;
      }
      // Search across every visible identifier so teachers can find a
      // student by surname even when they're shown by first name only.
      const haystack = [
        s.firstName,
        (s as any).lastName,
        (s as any).preferredName,
        (s as any).studentCode,
        s.yearLevel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(search.toLowerCase());
    })
    .slice()
    .sort((a, b) => studentSortKey(a as Student).localeCompare(studentSortKey(b as Student)));

  const clearMissingCodeFilter = () => {
    setMissingCodeActive(false);
    if (typeof window !== "undefined") {
      // Strip the param without leaving a back-button trap.
      const url = new URL(window.location.href);
      url.searchParams.delete("missing-code");
      window.history.replaceState(null, "", url.pathname + url.search);
    }
  };

  const clearMissingNameFilter = () => {
    setMissingNameActive(false);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("missing-name");
      window.history.replaceState(null, "", url.pathname + url.search);
    }
  };

  const handleSave = async (data: Omit<Student, "id">) => {
    try {
      if (editing) {
        await updateStudent.mutateAsync({ studentId: editing.id, data: data as any });
        queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
        toast({ title: "Student updated" });
      } else {
        await createStudent.mutateAsync({ data: data as any });
        queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
        toast({ title: "Student added" });
      }
      setIsOpen(false);
      setEditing(null);
    } catch (err) {
      toast({
        title: editing ? "Couldn't update the student" : "Couldn't add the student",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleArchive = async (student: Student) => {
    if (!confirm(`Archive ${formatStudentName(student, accountDefault)}? They will move to Past Students and can be restored later. Historical records and evidence will be kept.`)) return;
    try {
      await archiveStudent.mutateAsync({ studentId: student.id, data: { archived: true } });
      queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
      toast({ title: "Student archived" });
    } catch (err) {
      toast({
        title: "Couldn't archive the student",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleUnarchive = async (student: Student) => {
    try {
      await archiveStudent.mutateAsync({ studentId: student.id, data: { archived: false } });
      queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
      refetchArchived();
      toast({ title: "Student restored" });
    } catch (err) {
      toast({
        title: "Couldn't restore the student",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleExport = async (filter: "active" | "archived" | "all" = "active") => {
    setExporting(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ status: filter });
      const res = await fetch(apiUrl(`/api/students/export?${params}`), {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const text = await res.text();
      downloadBlob(text, "students.csv");
      toast({ title: "Export downloaded" });
    } catch (err) {
      // api-error-helper-exempt: raw fetch (not ApiError); surfaces helper-style fallback.
      toast({
        title: "Export failed",
        description: getApiErrorMessage(err, "Please try again in a moment."),
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Export CSV filter dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Export Students</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium">Students to include</Label>
              <Select
                value={exportFilter}
                onValueChange={(v) => setExportFilter(v as typeof exportFilter)}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active students only</SelectItem>
                  <SelectItem value="archived">Past / archived students only</SelectItem>
                  <SelectItem value="all">Active + past students (all)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                "Past / archived" refers to students year-end archived. Records are always preserved.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => { setExportDialogOpen(false); handleExport(exportFilter); }}
              disabled={exporting}
            >
              <Download className="h-4 w-4 mr-1.5" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Students</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setPastOpen(true)}
          >
            <History className="h-4 w-4" />
            Past students
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setExportDialogOpen(true)}
            disabled={exporting || !students?.length}
          >
            <Download className="h-4 w-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setImportOpen(true)}
          >
            <Upload className="h-4 w-4" />
            Import CSV
          </Button>
          <Button
            className="bg-pink-500 hover:bg-pink-600 text-white rounded-full h-11 px-5 shadow-md shadow-pink-500/20 gap-2"
            onClick={() => { setEditing(null); setIsOpen(true); }}
          >
            <Plus className="h-4 w-4" />
            Add Student
          </Button>
        </div>
      </div>
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input className="pl-9" placeholder="Search students..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {missingCodeActive && (
        <div
          className="mb-5 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800"
          data-testid="students-missing-code-filter"
        >
          <span>
            Showing only students <span className="font-semibold">without a student code</span>.
            Add a code to remove the export fallback warning.
          </span>
          <button
            type="button"
            onClick={clearMissingCodeFilter}
            className="ml-auto inline-flex items-center gap-1 text-amber-900 hover:underline"
            data-testid="students-missing-code-filter-clear"
          >
            <X className="h-3 w-3" />
            Clear filter
          </button>
        </div>
      )}
      {missingNameActive && (
        <div
          className="mb-5 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800"
          data-testid="students-missing-name-filter"
        >
          <span>
            Showing only students <span className="font-semibold">with no name recorded</span>.
            Add a first or last name to remove the export fallback warning.
          </span>
          <button
            type="button"
            onClick={clearMissingNameFilter}
            className="ml-auto inline-flex items-center gap-1 text-amber-900 hover:underline"
            data-testid="students-missing-name-filter-clear"
          >
            <X className="h-3 w-3" />
            Clear filter
          </button>
        </div>
      )}
      {isLoading && <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-xl" />)}</div>}
      {!isLoading && filtered.length === 0 && (
        <div className="py-24 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">{students?.length === 0 ? "No students yet" : "No students found"}</h3>
          <p className="text-gray-500 mb-4">Add students manually or import from a CSV file.</p>
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-1.5">
              <Upload className="h-4 w-4" />Import CSV
            </Button>
            <Button onClick={() => setIsOpen(true)} data-write-action>Add Student</Button>
          </div>
        </div>
      )}
      <div className="bg-white border rounded-xl overflow-hidden divide-y">
        {filtered.map((student) => (
          <div key={student.id} className="flex items-center px-5 py-3.5 hover:bg-gray-50 group transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-900">
                  {formatStudentName(student as Student, accountDefault)}
                </p>
                {student.schoolId != null ? (
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide">School</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-gray-500">Private</Badge>
                )}
              </div>
              {student.notes && <p className="text-xs text-gray-500 truncate mt-0.5">{student.notes}</p>}
            </div>
            {student.yearLevel && <Badge variant="secondary" className="text-xs mr-4">Year {student.yearLevel}</Badge>}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600" onClick={() => { setEditing(student as Student); setIsOpen(true); }}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-amber-600" onClick={() => handleArchive(student)} disabled={archiveStudent.isPending} title="Archive student">
                <Archive className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isOpen} onOpenChange={(v) => { setIsOpen(v); if (!v) setEditing(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Student" : "Add Student"}</DialogTitle></DialogHeader>
          <StudentForm initial={editing ?? undefined} onSave={handleSave} onClose={() => { setIsOpen(false); setEditing(null); }} isPending={createStudent.isPending || updateStudent.isPending} />
        </DialogContent>
      </Dialog>

      <Dialog open={pastOpen} onOpenChange={setPastOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Past students</DialogTitle>
          </DialogHeader>
          {archivedStudents.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No archived students yet. Students are moved here when a year-end rollover archives them.
            </p>
          ) : (
            <div className="border rounded-lg divide-y max-h-96 overflow-y-auto" data-testid="past-students-list">
              {archivedStudents.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center justify-between px-3 py-2.5"
                  data-testid={`past-student-row-${student.id}`}
                >
                  <div className="min-w-0">
                    <span className="text-sm font-medium">
                      {formatStudentName(student, accountDefault)}
                    </span>
                    {student.yearLevel && (
                      <span className="ml-2 text-xs text-gray-400">Year {student.yearLevel}</span>
                    )}
                    {student.schoolId != null ? (
                      <span className="ml-2 text-xs text-gray-400">· School</span>
                    ) : null}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs gap-1.5 text-blue-600"
                    onClick={() => handleUnarchive(student)}
                    disabled={archiveStudent.isPending}
                    data-testid={`button-unarchive-student-${student.id}`}
                    data-write-action
                  >
                    <ArchiveRestore className="h-3.5 w-3.5" />
                    Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPastOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportStudentsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => { refetch(); }}
      />
    </div>
  );
}
